import React, { useEffect, useMemo, useRef, useState } from 'react';
import { PopoverPanel } from './popover';
import { CalendarGrid } from './DatePicker';
import { TimePicker, formatTime12 } from './TimePicker';
import ChoiceKeys from './ChoiceKeys';
import Segmented from './Segmented';
import { Button } from './Button';
import { fmtMoveDate, fmtShortDate, daysBetween, isTripType, isSmallJobType, parseYmdLocal, loadDateLabel } from '../../lib/moves';

// ============================================================================
// TripStrip — THE move-date control (Move Dates Showroom pick 1B, Jaylen
// 2026-09-18). One card, tiles in the order the truck experiences them:
//
//     [ PACK  ]  [ LOAD * ]  [ UNLOAD ]
//      Sep 14      Sep 16      ~Sep 20–22
//
// Used on the client intake, the move header in the client file, the
// estimate editor, Quick Dispatch, the board's change-date dialog and the
// walkthrough app — never a bare DatePicker for a move date again.
//
// Rules (moves-architecture.md §4):
//   • Load is always there. Pack shows when packing is on the job
//     (packing_needed partial/full) or a pack date exists; otherwise a
//     small "+ Pack day". Unload shows for long-distance / storage or when
//     an unload date exists; local moves fold it to "+ Unload date".
//   • Tap a tile → its panel: the month grid (Full / Closed days shaded),
//     time, Confirmed / Tentative keys, and for Unload a Fixed day / Window
//     toggle (window = start then end in the same grid). Remove at the
//     bottom.
//   • Each date is tentative on its own (dashed amber tile, "~" before the
//     date). The footer reads the span: "Sep 14 → Sep 22 · 9 days · 3 on
//     the road / holding · ~ unload still tentative".
//
// Value = a move-shaped object (only the date fields are read):
//   { load_date, load_time, load_tentative, unload_date, unload_window_end,
//     unload_time, unload_tentative, pack_date, pack_time, pack_tentative,
//     job_type?, packing_needed?, storage_mode? }
// onChange(patch) gets ONLY the changed keys — callers merge (autosave /
// upsertMove take partials).
// ============================================================================

const DAYWORD = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const dow = (ymd) => { const d = parseYmdLocal(ymd); return d ? DAYWORD[d.getDay()] : ''; };

function Tile({ label, date, time, tentative, windowEnd, empty, emptyLabel, required, onOpen, innerRef, open, disabled, size }) {
    const has = !!date;
    const cls = [
        'relative text-left rounded-lg border px-2.5 py-2 min-h-[64px] flex flex-col gap-0.5 transition-colors w-full min-w-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500/20',
        has ? (tentative ? 'border-dashed border-amber-400 bg-amber-50' : 'border-slate-300 bg-white hover:border-slate-500') : 'border-dashed border-slate-300 bg-slate-50 hover:border-slate-500',
        open ? 'ring-2 ring-red-500/20 border-red-400' : '',
        disabled ? 'opacity-70 cursor-default' : 'cursor-pointer',
        size === 'sm' ? 'min-h-[56px] px-2 py-1.5' : '',
    ].join(' ');
    return (
        <button ref={innerRef} type="button" onClick={onOpen} disabled={disabled} className={cls} aria-haspopup="dialog" aria-expanded={open}>
            <span className="flex items-center justify-between gap-1">
                <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400">{label}{required && <span className="text-red-500"> *</span>}</span>
                {has && <span className={`text-[10px] font-bold ${tentative ? 'text-amber-700' : 'text-emerald-700'}`} title={tentative ? 'Tentative' : 'Confirmed'}>{tentative ? '~ tent' : '✓'}</span>}
            </span>
            {has ? (
                <>
                    <span className={`font-extrabold tracking-tight tabular-nums ${size === 'sm' ? 'text-[15px]' : 'text-[17px]'} ${tentative ? 'text-amber-900' : 'text-slate-900'}`}>{fmtMoveDate(date, { tentative: false, windowEnd })}</span>
                    <span className="text-[11px] text-slate-500 truncate">{[dow(date), time ? formatTime12(time) : null].filter(Boolean).join(' · ')}{tentative ? ' · tentative' : ''}</span>
                </>
            ) : (
                <span className={`text-[13px] font-bold ${empty ? 'text-slate-400' : 'text-slate-500'}`}>{emptyLabel}</span>
            )}
        </button>
    );
}

export function TripStrip({ value = {}, onChange, disabled = false, size = 'md', label = 'Move dates', showLabel = true, className = '', forcePack, forceUnload, hint = true }) {
    const v = value || {};
    const [open, setOpen] = useState(null); // 'pack' | 'load' | 'unload'
    const [winStep, setWinStep] = useState('start');
    // Window mode before any unload date exists (Jaylen 2026-09-18: picking
    // "Window" on an empty Unload tile snapped straight back to Fixed day,
    // because the only signal was a saved unload_window_end). Local until
    // the first pick writes the range.
    const [winMode, setWinMode] = useState(false);
    // One day / Load + unload days toggle (Jaylen 2026-09-18, after the
    // build): "+ Unload another day" as a text link read as an afterthought.
    // `wantMulti` shows the empty Unload tile before a date exists; a date,
    // a long-distance type or a storage mode make it multi-day on their
    // own. Reset when the strip is handed a different move.
    const [wantMulti, setWantMulti] = useState(false);
    const [wantPack, setWantPack] = useState(false);
    useEffect(() => { setWantMulti(false); setWantPack(false); setWinMode(false); setWinStep('start'); }, [v.id]);
    const refs = { pack: useRef(null), load: useRef(null), unload: useRef(null) };
    const change = (patch) => { if (!disabled) onChange?.(patch); };

    const small = isSmallJobType(v.job_type);
    const packingOn = v.packing_needed === 'partial' || v.packing_needed === 'full';
    const showPack = forcePack ?? (!small && (!!v.pack_date || packingOn || wantPack));
    const multiLocked = isTripType(v.job_type) || !!v.storage_mode || !!v.unload_date; // can't fold to one day without removing the unload
    const showUnload = forceUnload ?? (!small && (multiLocked || wantMulti));
    const setMode = (m) => {
        if (m === 'multi') { setWantMulti(true); setOpen('unload'); return; }
        setWantMulti(false);
        if (v.unload_date || v.unload_window_end) change({ unload_date: null, unload_window_end: null, unload_time: null, unload_tentative: false });
        if (open === 'unload') setOpen(null);
    };
    // "Move day" while the job loads and unloads on one day (most local
    // jobs — Jaylen 2026-09-18); "Load" once there's a separate unload.
    const loadLabel = showUnload && !small ? 'Load' : loadDateLabel(v);

    // Footer: the span in plain words.
    const foot = useMemo(() => {
        const first = v.pack_date || v.load_date; const last = v.unload_window_end || v.unload_date || v.load_date;
        if (!first || !last) return null;
        const days = (daysBetween(first, last) ?? 0) + 1;
        const road = v.load_date && v.unload_date ? Math.max(0, (daysBetween(v.load_date, v.unload_date) ?? 0) - 1) : 0;
        const soft = [v.pack_date && v.pack_tentative && 'pack', v.load_date && v.load_tentative && loadLabel.toLowerCase(), v.unload_date && v.unload_tentative && 'unload'].filter(Boolean);
        // One date = one day: say so instead of "Oct 6 → Oct 6 · 1 day".
        const text = days === 1 ? `${fmtShortDate(first)} · one day` : `${fmtShortDate(first)} → ${fmtShortDate(last)} · ${days} days${road ? ` · ${road} on the road / holding` : ''}`;
        return { text, soft };
    }, [v.pack_date, v.load_date, v.unload_date, v.unload_window_end, v.pack_tentative, v.load_tentative, v.unload_tentative, loadLabel]);

    // The controls row: One day | Load + unload days (Segmented, locked on
    // multi for long-distance / storage — the type decides) and a proper
    // "+ Pack day" button. Hidden for small job types and read-only strips.
    const controls = (!disabled && !small) ? (
        <span className="flex items-center gap-2 flex-wrap justify-end">
            {!showPack && <Button size="sm" variant="secondary" onClick={() => { setWantPack(true); setOpen('pack'); }}>+ Pack day</Button>}
            <Segmented size="sm" ariaLabel="One day or load and unload on different days"
                items={[{ value: 'one', label: 'One day', disabled: isTripType(v.job_type) || !!v.storage_mode, title: isTripType(v.job_type) ? 'Out-of-state moves always have an unload day' : undefined }, { value: 'multi', label: 'Load + unload days' }]}
                value={showUnload ? 'multi' : 'one'} onChange={setMode} />
        </span>
    ) : null;

    const tiles = [];
    if (showPack) tiles.push('pack');
    tiles.push('load');
    if (showUnload) tiles.push('unload');
    const cols = tiles.length;

    const keyFor = { pack: { date: 'pack_date', time: 'pack_time', tent: 'pack_tentative' }, load: { date: 'load_date', time: 'load_time', tent: 'load_tentative' }, unload: { date: 'unload_date', time: 'unload_time', tent: 'unload_tentative' } };

    const panelFor = (k) => {
        const f = keyFor[k];
        const date = v[f.date] || '';
        const isWindow = k === 'unload' && (!!v.unload_window_end || winMode);
        const pick = (ymd) => {
            if (k === 'unload' && isWindow) {
                // First pick (or "From" step) sets the start; the end lands on
                // the same day until the second pick stretches it.
                if (winStep === 'start' || !date) { change({ unload_date: ymd, unload_window_end: (v.unload_window_end && v.unload_window_end >= ymd) ? v.unload_window_end : ymd }); setWinStep('end'); }
                else { if (ymd < date) change({ unload_date: ymd, unload_window_end: date }); else change({ unload_window_end: ymd }); setWinStep('start'); }
                return;
            }
            change({ [f.date]: ymd });
            if (k === 'load' && !date && v.unload_date && v.unload_date < ymd) change({ unload_date: null, unload_window_end: null });
            if (!small && k === 'load') { /* keep the panel open so time / tentative can follow */ }
        };
        return (
            <div className="p-3 w-[304px]">
                <div className="flex items-center justify-between mb-1">
                    <div className="text-[11px] font-extrabold uppercase tracking-wider text-slate-500">{k === 'pack' ? 'Pack day' : k === 'load' ? loadLabel : 'Unload'}</div>
                    {k === 'unload' && (
                        <Segmented size="sm" ariaLabel="Fixed day or window" items={[{ value: 'day', label: 'Fixed day' }, { value: 'window', label: 'Window' }]} value={isWindow ? 'window' : 'day'}
                            onChange={(m) => {
                                if (m === 'window') { setWinMode(true); if (date) change({ unload_window_end: date }); setWinStep(date ? 'end' : 'start'); }
                                else { setWinMode(false); setWinStep('start'); if (v.unload_window_end) change({ unload_window_end: null }); }
                            }} />
                    )}
                </div>
                {k === 'unload' && isWindow && (
                    <div className="flex gap-1.5 mb-1.5">
                        {['start', 'end'].map(s => (
                            <button key={s} type="button" onClick={() => setWinStep(s)} className={`flex-1 h-7 rounded-md text-[11px] font-bold border ${winStep === s ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200'}`}>
                                {s === 'start' ? `From ${date ? fmtShortDate(date) : '…'}` : `To ${v.unload_window_end ? fmtShortDate(v.unload_window_end) : '…'}`}
                            </button>
                        ))}
                    </div>
                )}
                <CalendarGrid value={date} rangeEnd={isWindow ? v.unload_window_end : null} onPick={pick} min={k !== 'pack' && k !== 'load' ? (v.load_date || undefined) : undefined} max={k === 'pack' && v.load_date ? v.load_date : undefined} />
                <div className="grid grid-cols-[1fr_auto] gap-2 items-center mt-2">
                    <TimePicker size="sm" value={v[f.time] || ''} onChange={(t) => change({ [f.time]: t || null })} ariaLabel={`${k} time`} placeholder="Time (optional)" clearable />
                    <ChoiceKeys size="sm" ariaLabel={`${k} confirmed or tentative`} items={[{ value: 'ok', label: 'Confirmed' }, { value: 'tent', label: '~ Tentative', color: '#b45309' }]} value={v[f.tent] ? 'tent' : 'ok'} onChange={(x) => change({ [f.tent]: x === 'tent' })} />
                </div>
                <div className="flex items-center justify-between mt-2">
                    {date && k !== 'load' ? (
                        <button type="button" className="text-[11.5px] font-semibold text-red-600 hover:underline" onClick={() => { change(k === 'pack' ? { pack_date: null, pack_time: null, pack_tentative: false } : { unload_date: null, unload_window_end: null, unload_time: null, unload_tentative: false }); if (k === 'pack') setWantPack(false); else { setWantMulti(false); setWinMode(false); } setOpen(null); }}>Remove {k === 'pack' ? 'pack day' : 'unload day'}</button>
                    ) : date && k === 'load' ? (
                        <button type="button" className="text-[11.5px] font-semibold text-red-600 hover:underline" onClick={() => { change({ load_date: null, load_time: null, load_tentative: false }); setOpen(null); }}>Clear date</button>
                    ) : <span />}
                    <Button size="sm" variant="secondary" onClick={() => setOpen(null)}>Done</Button>
                </div>
            </div>
        );
    };

    return (
        <div className={`rounded-lg border border-slate-200 bg-white ${size === 'sm' ? 'p-2.5' : 'p-3'} ${className}`}>
            {(showLabel || controls) && (
                <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                    <span className="text-[11px] font-bold text-slate-600 uppercase tracking-wide">{showLabel ? label : ''}</span>
                    {controls}
                </div>
            )}
            <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
                {tiles.map(k => {
                    const f = keyFor[k];
                    return (
                        <Tile key={k} innerRef={refs[k]} label={k === 'pack' ? 'Pack' : k === 'load' ? loadLabel : 'Unload'} required={k === 'load'}
                            date={v[f.date]} time={v[f.time]} tentative={!!v[f.tent]} windowEnd={k === 'unload' ? v.unload_window_end : null}
                            empty emptyLabel={k === 'load' ? 'Pick a date' : k === 'pack' ? 'Pick a date' : (small ? '' : 'Pick a date')}
                            onOpen={() => setOpen(o => o === k ? null : k)} open={open === k} disabled={disabled} size={size} />
                    );
                })}
            </div>
            {/* Hidden anchors for "+ Pack day" / "+ Unload date" when the tile isn't rendered */}
            {!showPack && <span ref={refs.pack} className="block h-0" />}
            {!showUnload && <span ref={refs.unload} className="block h-0" />}
            {open && <PopoverPanel open onClose={() => setOpen(null)} anchorRef={refs[open]} ariaLabel={`${open} date`}>{panelFor(open)}</PopoverPanel>}
            {foot && hint && (
                <div className="flex items-center justify-between gap-2 mt-2 text-[11.5px] text-slate-600 flex-wrap">
                    <span className="font-semibold">{foot.text}</span>
                    <span className={foot.soft.length ? 'text-amber-700 font-semibold' : 'text-slate-400'}>{foot.soft.length ? `~ ${foot.soft.join(', ')} still tentative` : 'all dates confirmed'}</span>
                </div>
            )}
        </div>
    );
}

export default TripStrip;
